import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { StyledTabs } from "../../components/Base";

// crewId/userId are absent on the self route (/user/compliance/:pageId).
interface MatchParams {
  crewId?: string;
  userId?: string;
  pageId: string;
}

interface UserNavigationProps extends RouteComponentProps<MatchParams> {
  // Marks the Positions/Operations tab with a warning triangle when that tab
  // has profile-setup findings, so issues on the tab the user is NOT viewing
  // stay visible (CREW-530).
  profileSetupWarnings?: {
    positions: boolean;
    operations: boolean;
  };
}

const UserNavigation: FC<UserNavigationProps> = ({ match, history, profileSetupWarnings }) => {
  const { t } = useTranslation();
  const { crewId, userId, pageId } = match.params;

  const pages = useMemo(() => ["positions", "operations", "aircraft", "all", "bundles"], []);

  const getPageIndex = (subpage: string): number => Math.max(0, pages.indexOf(subpage));
  const getPageByIndex = (i: number): string => pages[i];

  const navigateTo = (_: React.SyntheticEvent, value: number) => {
    const subpage = getPageByIndex(value);
    history.replace(crewId ? `/crew/${crewId}/members/${userId}/compliance/${subpage}` : `/user/compliance/${subpage}`);
  };

  const withWarning = (label: ReactNode, warn: boolean | undefined): ReactNode =>
    warn ? (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {label}
        <WarningAmberIcon fontSize="small" color="warning" titleAccess={t("compliance.action_required")} />
      </Box>
    ) : (
      label
    );

  return (
    <StyledTabs variant="fullWidth" value={getPageIndex(pageId)} indicatorColor="primary" onChange={navigateTo}>
      <Tab label={withWarning(t("compliance.positions"), profileSetupWarnings?.positions)} />
      <Tab label={withWarning(t("compliance.operations"), profileSetupWarnings?.operations)} />
      <Tab label={t("compliance.aircraft")} />
      <Tab label={t("compliance.all_courses")} />
      <Tab label={t("compliance.bundles")} />
    </StyledTabs>
  );
};

export default UserNavigation;
